<script setup lang="ts">
/**
 * Column chart: one column per category, one or more series — stacked (default)
 * or grouped side by side. Negative values grow down from the zero line on the
 * same scale, so a column's height always means the same thing.
 *
 * The chart fills its box (give it a height) and draws at the box's real pixel
 * size. Hovering a category — or walking them with the arrow keys once the
 * chart has focus — highlights its column and shows every series' value in a
 * tooltip. Two or more series get a legend; hovering an entry brings that
 * series forward. Screen readers get the same values as a hidden table.
 *
 * Presentational only: categories and series come in as props.
 */
import { computed, inject, ref, useTemplateRef } from 'vue';
import { i18nInjectionKey } from '../internal';
import { valueAxis } from './axis';
import { plotFrame, thinLabels, AXIS_CHAR_PX } from './frame';
import { barPath } from './geometry';
import { announceTooltip } from './announce';
import NxChartLegend from './NxChartLegend.vue';
import NxChartLive from './NxChartLive.vue';
import NxChartTable from './NxChartTable.vue';
import NxChartTooltip, { type NxChartTooltipRow } from './NxChartTooltip.vue';
import {
  chartColor,
  chartValue,
  seriesExtent,
  stackColumns,
  type NxChartKey,
  type NxChartSeries,
} from './series';
import { useChartSize } from './useChartSize';

const props = withDefaults(
  defineProps<{
    /** Category labels, left to right. */
    categories: readonly string[];
    /** Series, bottom to top in a stack; each has one value per category. */
    series: readonly NxChartSeries[];
    /** `stacked` piles the series in one column, `grouped` sets them side by side. */
    mode?: 'stacked' | 'grouped';
    /** Accessible name of the chart („Umsatz nach Monat“). */
    ariaLabel?: string;
    /**
     * Values on the column ends: `auto` for a single series while they fit,
     * `none` never. Stacks never get them on their segments.
     */
    valueLabels?: 'auto' | 'none';
    /** Legend under the plot: `auto` shows it from two series on. */
    legend?: 'auto' | 'none';
    /** Formats values (tooltip, column ends, table). Defaults to the app locale's number. */
    formatValue?: (value: number) => string;
    /** Formats the value-axis ticks. Defaults to round, compact tick labels. */
    formatTick?: (value: number) => string;
    /** Caption of the tooltip's sum line („Summe“). */
    totalLabel?: string;
    /** Locale for the default number formats. Falls back to the host app's i18n locale. */
    locale?: string;
    /** Spoken hint for keyboard users, appended to the plot's name. */
    keyboardHint?: string;
  }>(),
  {
    mode: 'stacked',
    ariaLabel: undefined,
    valueLabels: 'auto',
    legend: 'auto',
    formatValue: undefined,
    formatTick: undefined,
    totalLabel: 'Summe',
    locale: undefined,
    keyboardHint: 'Pfeiltasten wechseln die Kategorie',
  }
);

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value);
const numberFormat = computed(() => new Intl.NumberFormat(locale.value));
function format(value: number): string {
  return props.formatValue ? props.formatValue(value) : numberFormat.value.format(value);
}

const plotRef = useTemplateRef<HTMLElement>('plot');
const { width, height } = useChartSize(plotRef, { width: 480, height: 220 });

/** Max column (or grouped bar) thickness in px: thin marks, the band's rest is air. */
const MAX_BAR = 28;
const MAX_GROUPED_BAR = 16;
const RADIUS = 4;
/** Surface gap between touching marks. */
const GAP = 2;

const stacked = computed(() => props.mode === 'stacked' || props.series.length < 2);
const stack = computed(() => stackColumns(props.categories, props.series));
const axis = computed(() => {
  const ext = stacked.value ? stack.value : seriesExtent(props.series);
  return valueAxis(ext.min, ext.max);
});
const frame = computed(() =>
  plotFrame(axis.value, width.value, height.value, {
    locale: locale.value,
    formatTick: props.formatTick,
  })
);
const band = computed(() => frame.value.width / Math.max(1, props.categories.length));
const zeroY = computed(() => frame.value.y(0));

interface DrawnBar {
  key: string;
  seriesKey: NxChartKey;
  seriesIndex: number;
  category: number;
  d: string;
  color: string;
  negative: boolean;
}

const bars = computed<DrawnBar[]>(() => {
  const fr = frame.value;
  const b = band.value;
  const out: DrawnBar[] = [];
  if (stacked.value) {
    const w = Math.max(2, Math.min(MAX_BAR, b * 0.62));
    for (const col of stack.value.columns) {
      const x = fr.left + col.index * b + (b - w) / 2;
      const pos = col.segments.filter((s) => s.value >= 0 && s.value !== 0);
      const neg = col.segments.filter((s) => s.value < 0);
      for (const group of [pos, neg]) {
        group.forEach((s, i) => {
          const outer = i === group.length - 1;
          let y0 = fr.y(s.from);
          let y1 = fr.y(s.to);
          // Surface gap between neighbours: trim the inner joint of each segment.
          const dir = y1 < y0 ? -1 : 1;
          if (i > 0) y0 += (dir * GAP) / 2;
          if (!outer) y1 -= (dir * GAP) / 2;
          if ((y1 - y0) * dir <= 0.5) y1 = y0 + dir * 0.5;
          out.push({
            key: `${col.index}:${s.seriesIndex}`,
            seriesKey: s.key,
            seriesIndex: s.seriesIndex,
            category: col.index,
            d: barPath(x, y0, y1, w, outer ? RADIUS : 0),
            color: s.color,
            negative: s.value < 0,
          });
        });
      }
    }
    return out;
  }
  const n = props.series.length;
  const w = Math.max(2, Math.min(MAX_GROUPED_BAR, (b * 0.72 - GAP * (n - 1)) / n));
  const groupW = w * n + GAP * (n - 1);
  props.categories.forEach((_, ci) => {
    const x0 = fr.left + ci * b + (b - groupW) / 2;
    props.series.forEach((s, si) => {
      const v = chartValue(s.values[ci]);
      if (v === null || v === 0) return;
      out.push({
        key: `${ci}:${si}`,
        seriesKey: s.key,
        seriesIndex: si,
        category: ci,
        d: barPath(x0 + si * (w + GAP), fr.y(0), fr.y(v), w, RADIUS),
        color: chartColor(s.color, si),
        negative: v < 0,
      });
    });
  });
  return out;
});

const xLabels = computed(() => {
  const fr = frame.value;
  return thinLabels(props.categories, band.value).map((l) => ({
    ...l,
    x: fr.left + (l.index + 0.5) * band.value,
  }));
});

/** Values on the column ends — single series only, and only while they fit. */
const endLabels = computed(() => {
  if (props.valueLabels === 'none' || props.series.length !== 1) return [];
  const fr = frame.value;
  const texts = stack.value.columns.map((c) => (c.hasValue ? format(c.net) : ''));
  const longest = Math.max(0, ...texts.map((t) => t.length));
  if (longest * AXIS_CHAR_PX + 4 > band.value) return [];
  return stack.value.columns
    .filter((c) => c.hasValue)
    .map((c) => ({
      key: c.index,
      x: fr.left + (c.index + 0.5) * band.value,
      y: c.net < 0 ? fr.y(c.net) + 12 : fr.y(c.net) - 5,
      text: texts[c.index]!,
    }));
});

// ── Hover & keyboard ─────────────────────────────────────────────────────────
const active = ref<number | null>(null);
const highlightedSeries = ref<NxChartKey | null>(null);

function onPointer(event: PointerEvent) {
  const box = plotRef.value?.getBoundingClientRect();
  if (!box) return;
  const x = event.clientX - box.left - frame.value.left;
  const i = Math.floor(x / band.value);
  active.value = i >= 0 && i < props.categories.length ? i : null;
}

function onKeydown(event: KeyboardEvent) {
  const n = props.categories.length;
  if (!n) return;
  const cur = active.value;
  let next: number;
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      next = cur === null ? 0 : Math.min(cur + 1, n - 1);
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      next = cur === null ? n - 1 : Math.max(cur - 1, 0);
      break;
    case 'Home':
      next = 0;
      break;
    case 'End':
      next = n - 1;
      break;
    case 'Escape':
      active.value = null;
      return;
    default:
      return;
  }
  event.preventDefault();
  active.value = next;
}

const tooltip = computed(() => {
  const i = active.value;
  if (i === null) return null;
  const fr = frame.value;
  const col = stack.value.columns[i];
  if (!col) return null;
  const rows: NxChartTooltipRow[] = props.series
    .map((s, si) => {
      const v = chartValue(s.values[i]);
      return {
        key: s.key,
        label: s.label,
        value: v === null ? '–' : format(v),
        color: chartColor(s.color, si),
        muted: v === null,
      };
    })
    .reverse(); // top of the stack first, as drawn
  const top = stacked.value
    ? fr.y(Math.max(0, ...col.segments.map((s) => Math.max(s.to, s.from))))
    : fr.y(Math.max(0, ...props.series.map((s) => chartValue(s.values[i]) ?? 0)));
  return {
    x: fr.left + (i + 0.5) * band.value,
    y: Math.max(fr.top + 16, Math.min(top, fr.bottom - 16)),
    title: props.categories[i] ?? '',
    rows: props.series.length === 1 ? rows.map((r) => ({ ...r, color: undefined })) : rows,
    footer:
      props.series.length > 1 && stacked.value && col.hasValue
        ? `${props.totalLabel} ${format(col.net)}`
        : undefined,
  };
});

// ── Legend & table ───────────────────────────────────────────────────────────
/** What the live region says: only while the plot has keyboard focus. */
const focused = ref(false);
const announcement = computed(() => (focused.value ? announceTooltip(tooltip.value) : ''));
const plotLabel = computed(() => [props.ariaLabel, props.keyboardHint].filter(Boolean).join('. '));

const legendItems = computed(() =>
  props.series.map((s, i) => ({ key: s.key, label: s.label, color: chartColor(s.color, i) }))
);
const showLegend = computed(() => props.legend === 'auto' && props.series.length > 1);

const table = computed(() => ({
  columns: ['', ...props.series.map((s) => s.label)],
  rows: props.categories.map((c, i) => ({
    key: i,
    cells: [
      c,
      ...props.series.map((s) => {
        const v = chartValue(s.values[i]);
        return v === null ? '–' : format(v);
      }),
    ],
  })),
}));

function barClass(bar: DrawnBar) {
  return {
    'is-negative': bar.negative,
    'is-dimmed':
      (highlightedSeries.value !== null && highlightedSeries.value !== bar.seriesKey) ||
      (active.value !== null && active.value !== bar.category),
  };
}
</script>

<template>
  <div class="nx-bar-chart" role="group" :aria-label="props.ariaLabel">
    <div
      ref="plot"
      class="nx-bar-chart__plot"
      tabindex="0"
      role="group"
      :aria-label="plotLabel"
      @pointermove="onPointer"
      @pointerleave="active = null"
      @keydown="onKeydown"
      @focus="focused = true"
      @blur="
        focused = false;
        active = null;
      "
    >
      <svg
        class="nx-bar-chart__svg"
        :width="width"
        :height="height"
        :viewBox="`0 0 ${width} ${height}`"
        aria-hidden="true"
      >
        <g class="nx-bar-chart__grid">
          <line
            v-for="tick in frame.tickLabels"
            :key="tick.value"
            :x1="frame.left"
            :x2="frame.right"
            :y1="tick.y"
            :y2="tick.y"
            :class="{ 'is-zero': tick.value === 0 }"
          />
        </g>
        <g class="nx-bar-chart__ticks">
          <text
            v-for="tick in frame.tickLabels"
            :key="tick.value"
            :x="frame.left - 8"
            :y="tick.y"
            text-anchor="end"
            dominant-baseline="middle"
          >
            {{ tick.text }}
          </text>
        </g>
        <rect
          v-if="active !== null"
          class="nx-bar-chart__band"
          :x="frame.left + active * band + 2"
          :y="frame.top"
          :width="Math.max(0, band - 4)"
          :height="frame.height"
          :rx="4"
        />
        <g class="nx-bar-chart__bars" :style="{ '--zero': `${zeroY}px` }">
          <path
            v-for="bar in bars"
            :key="bar.key"
            class="nx-bar-chart__bar"
            :class="barClass(bar)"
            :d="bar.d"
            :style="{ fill: bar.color }"
          />
        </g>
        <g class="nx-bar-chart__values">
          <text
            v-for="label in endLabels"
            :key="label.key"
            :x="label.x"
            :y="label.y"
            text-anchor="middle"
          >
            {{ label.text }}
          </text>
        </g>
        <g class="nx-bar-chart__xlabels">
          <text
            v-for="label in xLabels"
            :key="label.index"
            :x="label.x"
            :y="height - 6"
            text-anchor="middle"
            :class="{ 'is-active': active === label.index }"
          >
            {{ label.text }}
          </text>
        </g>
      </svg>
      <NxChartTooltip
        v-if="tooltip"
        :x="tooltip.x"
        :y="tooltip.y"
        :box-width="width"
        :title="tooltip.title"
        :rows="tooltip.rows"
        :footer="tooltip.footer"
      />
      <NxChartLive :text="announcement" />
    </div>
    <NxChartLegend v-if="showLegend" v-model:highlighted="highlightedSeries" :items="legendItems" />
    <NxChartTable :caption="props.ariaLabel" :columns="table.columns" :rows="table.rows" />
  </div>
</template>

<style scoped>
.nx-bar-chart {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  width: 100%;
  height: 100%;
  min-height: calc(var(--nxt-size-unit) * 8);
  min-width: 0;
}
.nx-bar-chart__plot {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  border-radius: var(--nxt-radius-sm);
  outline: none;
}
.nx-bar-chart__plot:focus-visible {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-bar-chart__svg {
  position: absolute;
  inset: 0;
  display: block;
  overflow: visible;
}
.nx-bar-chart__grid line {
  stroke: var(--nxt-chart-grid);
  stroke-width: 1;
  shape-rendering: crispEdges;
}
.nx-bar-chart__grid line.is-zero {
  stroke: var(--nxt-chart-baseline);
}
.nx-bar-chart__ticks text,
.nx-bar-chart__xlabels text {
  fill: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
}
.nx-bar-chart__xlabels text {
  transition: fill var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-bar-chart__xlabels text.is-active {
  fill: var(--nxt-fg);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-bar-chart__values text {
  fill: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  font-variant-numeric: tabular-nums;
}
.nx-bar-chart__band {
  fill: var(--nxt-chart-grid);
}
.nx-bar-chart__bar {
  transform-box: view-box;
  transform-origin: 0 var(--zero);
  transition: opacity var(--nxt-motion-fast) var(--nxt-ease-out);
  animation: nx-bar-grow var(--nxt-motion-slow) var(--nxt-ease-out) both;
}
.nx-bar-chart__bar.is-dimmed {
  opacity: 0.35;
}
@keyframes nx-bar-grow {
  from {
    transform: scaleY(0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-bar-chart__bar,
  .nx-bar-chart__xlabels text {
    animation: none;
    transition: none;
  }
}
@media (forced-colors: active) {
  .nx-bar-chart__bar {
    fill: CanvasText !important;
  }
}
</style>
